import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, Card, Empty, Select, Skeleton, Space, Tag, Typography } from "antd";
import {
  BookOutlined,
  CarOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  LeftOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  RightOutlined,
  SafetyCertificateOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import OnTheWayButton from "../notifications/OnTheWayButton";
import { createReport } from "../workReports/api";
import { getMyDay } from "./api";
import { addDays, dayOf } from "./calc";
import { absenceLabels, dayLabel, kindLabels, timeRange } from "./labels";
import "./dispatch.css";

/** One job of the technician's day with everything needed on the way and on site. */
const JobCard = ({ item, day, can, onWorkReport }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const kinds = kindLabels(t);
  return (
    <Card className="dsp-myjob" size="small">
      <div className="dsp-myjob__head">
        <span className="dsp-myjob__time">{timeRange(item)}</span>
        <Tag>{kinds[item.kind]}</Tag>
      </div>
      <Typography.Title level={5} className="dsp-myjob__title">
        {item.title}
      </Typography.Title>
      {item.client && (
        <div className="dsp-myjob__row">
          <strong>{item.client.name}</strong>
          {item.branch?.name && <span> · {item.branch.name}</span>}
          <Space wrap size={4} className="dsp-myjob__contact">
            {item.client.phone && (
              <Button size="small" icon={<PhoneOutlined />} href={`tel:${item.client.phone}`}>
                {item.client.phone}
              </Button>
            )}
            {item.client.email && (
              <Button size="small" icon={<MailOutlined />} href={`mailto:${item.client.email}`}>
                {t("Email")}
              </Button>
            )}
          </Space>
        </div>
      )}
      {item.address && (
        <div className="dsp-myjob__row">
          <EnvironmentOutlined /> {item.address}
        </div>
      )}
      {item.vehicleInfo && (
        <div className="dsp-myjob__row">
          <CarOutlined />{" "}
          {[item.vehicleInfo.name, item.vehicleInfo.plate].filter(Boolean).join(" · ")}
          {item.driverName && ` · ${t("Driver")}: ${item.driverName}`}
        </div>
      )}
      {item.colleagues?.length > 0 && (
        <div className="dsp-myjob__row dsp-small">
          {t("With {{names}}", { names: item.colleagues.join(", ") })}
        </div>
      )}
      {item.materials && (
        <div className="dsp-myjob__row">
          <Typography.Text strong>{t("Material to bring")}</Typography.Text>
          <div className="dsp-pre">{item.materials}</div>
        </div>
      )}
      {item.notes && (
        <div className="dsp-myjob__row dsp-pre">
          <Typography.Text type="secondary">{item.notes}</Typography.Text>
        </div>
      )}
      <div className="dsp-myjob__actions">
        {item.mapsUrl && (
          <Button
            type="primary"
            icon={<EnvironmentOutlined />}
            href={item.mapsUrl}
            target="_blank"
            rel="noreferrer"
          >
            {t("Navigate")}
          </Button>
        )}
        {item.notifyCustomer && item.client && <OnTheWayButton appointmentId={item.appointment} />}
        {can.workReport && (
          <Button icon={<FileDoneOutlined />} onClick={() => onWorkReport(item)}>
            {t("Work report")}
          </Button>
        )}
        {can.siteDiary && item.project && (
          <Button
            icon={<BookOutlined />}
            onClick={() => history.push(`/user/site-diary/${item.project.id}/${day}`)}
          >
            {t("Site diary")}
          </Button>
        )}
        {can.maintenance && item.maintenanceVisit && (
          <Button
            icon={<SafetyCertificateOutlined />}
            onClick={() => history.push(`/user/maintenance/visits/${item.maintenanceVisit.id}`)}
          >
            {t("Maintenance protocol")}
          </Button>
        )}
      </div>
    </Card>
  );
};

/**
 * "Mein Tag" (/user/my-day): the signed-in employee's jobs today and tomorrow. Works from the
 * offline copy when there is no connection. The owner or a dispatcher picks an employee.
 */
const MyDay = () => {
  const { t, i18n } = useTranslation("dashboard");
  const history = useHistory();
  const [date, setDate] = useState(dayOf(new Date()));
  const [employee, setEmployee] = useState(null);
  const [data, setData] = useState(null);
  const [cachedAt, setCachedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const absences = absenceLabels(t);

  const load = () => {
    setLoading(true);
    getMyDay({ date, employee })
      .then(res => {
        setData(res.data.data);
        setCachedAt(res.fromCache ? res.cachedAt : null);
      })
      .catch(generalErrorAlert)
      .finally(() => setLoading(false));
  };

  useEffect(load, [date, employee]);

  const workReport = async item => {
    try {
      const res = await createReport({
        date: dayOf(item.start),
        ...(item.project ? { project: item.project.id } : {}),
        ...(item.client ? { client: item.client.id } : {}),
        siteAddress: item.address || "",
        workDescription: item.title
      });
      history.push(`/user/work-reports/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const today = dayOf(new Date());
  const relative = day =>
    day === today ? t("Today") : day === addDays(today, 1) ? t("Tomorrow") : "";

  return (
    <div className="dispatch-page dsp-myday">
      <Space wrap className="dsp-myday__bar">
        {data?.staff?.length > 0 && (
          <Select
            className="dsp-myday__who"
            placeholder={t("Choose an employee")}
            value={employee || data?.employee?.id || undefined}
            onChange={setEmployee}
            options={data.staff.map(s => ({ value: s.id, label: s.name }))}
          />
        )}
        <Button icon={<ReloadOutlined />} onClick={load} aria-label={t("Reload")} />
      </Space>
      {cachedAt && (
        <Alert
          type="info"
          showIcon
          className="dsp-section"
          message={t("Offline copy from {{time}}", {
            time: new Date(cachedAt).toLocaleString(i18n.language, {
              dateStyle: "short",
              timeStyle: "short"
            })
          })}
        />
      )}
      {loading && !data ? (
        <Skeleton active />
      ) : !data?.employee ? (
        <Empty description={t("Choose an employee to see their day")} />
      ) : (
        data.days.map(d => (
          <section key={d.day} className="dsp-myday__day">
            <Typography.Title level={4}>
              {relative(d.day) ? `${relative(d.day)} · ` : ""}
              {dayLabel(d.day, i18n.language, { long: true })}
            </Typography.Title>
            {d.absences.map((a, i) => (
              <Tag key={i} color="orange" className="dsp-absence">
                {absences[a.kind] || a.label}
              </Tag>
            ))}
            {d.items.length === 0 ? (
              <Typography.Paragraph type="secondary">{t("No jobs planned")}</Typography.Paragraph>
            ) : (
              d.items.map(item => (
                <JobCard
                  key={item.id}
                  item={item}
                  day={d.day}
                  can={data.can}
                  onWorkReport={workReport}
                />
              ))
            )}
          </section>
        ))
      )}
      {data?.employee && (
        <Space className="dsp-section">
          <Button
            icon={<LeftOutlined className="dsp-flip" />}
            aria-label={t("Back")}
            onClick={() => setDate(addDays(date, -1))}
          />
          <Button onClick={() => setDate(today)} disabled={date === today}>
            {t("Today")}
          </Button>
          <Button
            icon={<RightOutlined className="dsp-flip" />}
            aria-label={t("Next")}
            onClick={() => setDate(addDays(date, 1))}
          />
        </Space>
      )}
    </div>
  );
};

export default MyDay;
